// You can write more code here

/* START OF COMPILED CODE */

/* START-USER-IMPORTS */
import {
  DEFAULT_TEXTURES,
  DefaultCharacterTextures,
} from '@/common/types/game';
import { EquipmentConfiguration } from '@/common/types/student';
import { getCharacterInventory } from '@/game/GameComponents/PhaserGame/GameInstance';
import EventsManager from '@/game/GameComponents/PhaserGame/Managers/EventsManager';
import {
  getCustomizationFromId,
  getDollViewPath,
} from '@/game/GameComponents/PhaserGame/Utils/CosmeticUtils';
import GAME_EVENTS from '@/game/GameComponents/PhaserGame/Utils/gameEventsDef';
/* END-USER-IMPORTS */

export default class PlayerDoll extends Phaser.GameObjects.Container {
  constructor(scene: Phaser.Scene, x?: number, y?: number) {
    super(scene, x ?? 0, y ?? 0);

    this.scaleX = 6;
    this.scaleY = 6;

    // back
    const back = scene.add.image(0, 0, 'ui-grey-cloak-small');
    back.name = 'back';
    this.add(back);

    // complexion
    const complexion = scene.add.image(0, 0, 'ui-complexion-1-small');
    complexion.name = 'complexion';
    this.add(complexion);

    // shoes
    const shoes = scene.add.image(0, 1, 'ui-black-boots-horned-small');
    shoes.name = 'shoes';
    this.add(shoes);

    // pants
    const pants = scene.add.image(0, 1, 'ui-black-long-pants-small');
    pants.name = 'pants';
    this.add(pants);

    // clothes
    const clothes = scene.add.image(0, 1, 'ui-black-ridged-hitek-top-small');
    clothes.name = 'clothes';
    this.add(clothes);

    // hair
    const hair = scene.add.image(0, 0, 'ui-black-mullet-small');
    hair.name = 'hair';
    this.add(hair);

    // face
    const face = scene.add.image(0, 1, 'ui-iron-mask-small');
    face.name = 'face';
    this.add(face);

    // hats
    const hats = scene.add.image(0, 0, 'ui-straw-hat-small');
    hats.name = 'hats';
    this.add(hats);

    // gloves
    const gloves = scene.add.image(0, 0, 'ui-black-hitek-bracers-small');
    gloves.name = 'gloves';
    this.add(gloves);

    /* START-USER-CTR-CODE */
    this.initialize();

    EventsManager.registerEventOn(
      this.scene,
      GAME_EVENTS.PLAYER_EQUIPPED_COSMETIC,
      this.handleNewCosmeticEquipped,
      this,
    );
    EventsManager.registerEventOn(
      this.scene,
      GAME_EVENTS.PLAYER_UNEQUIPPED_COSMETIC,
      this.handleNewCosmeticEquipped,
      this,
    );
    this.scene.load.on('complete', this.refresh);
    /* END-USER-CTR-CODE */
  }

  /* START-USER-CODE */

  private overrides?: Partial<EquipmentConfiguration> = {};

  initialize() {
    this.setVisible(false);
    EventsManager.registerEventOn(
      this.scene,
      GAME_EVENTS.PLAYER_TEXTURES_FINISHED_REFRESH,
      this.refresh,
      this,
    );
    this.refresh();
  }

  refresh() {
    if (this.overrides === undefined) return;
    this.setVisible(true);
    this.list.forEach((child) => {
      if (child instanceof Phaser.GameObjects.Image) child.setVisible(false);
    });
    let playerSetup = getCharacterInventory().equippedItems;

    Object.keys(playerSetup).forEach((key: string) => {
      let customizationId = playerSetup[key as keyof EquipmentConfiguration];
      const override = this.overrides![key as keyof EquipmentConfiguration];
      if (override !== undefined) {
        customizationId = override;
      }

      if (customizationId === null) {
        // Hide the slot.

        const slotObj = this.list.find((child) => child.name === `${key}`);
        const slotSprite = slotObj as Phaser.GameObjects.Image;
        const customizationName =
          DEFAULT_TEXTURES[key as keyof DefaultCharacterTextures];

        if ((slotSprite as Phaser.GameObjects.Image) && slotObj) {
          slotSprite.setVisible(false);
          if (key === 'clothes' || key === 'pants' || key === 'complexion') {
            if (this.scene.textures.exists(customizationName.dollView)) {
              slotSprite.setTexture(customizationName.dollView);
              slotSprite.setVisible(true);
            }
          }
        }
      } else {
        // Override the slot.

        const customizationData = getCustomizationFromId(customizationId);
        if (!customizationData) return;
        const customizationName =
          getDollViewPath(customizationData) ??
          DEFAULT_TEXTURES[key as keyof DefaultCharacterTextures]?.dollView;

        // Find the child game object with the corresponding slot.
        const slotObj = this.list.find(
          (child) => child.name === `${customizationData?.slot}`,
        );
        const slotSprite = slotObj as Phaser.GameObjects.Image;
        if ((slotSprite as Phaser.GameObjects.Image) && slotObj) {
          if (this.scene.textures.exists(customizationName)) {
            slotSprite.setVisible(true);
            slotSprite.setTexture(customizationName);
          }
        }
      }
    });

    // Do slot hiding for the equipped items.
    // Slot hiding step.
    Object.keys(playerSetup).forEach((key: string) => {
      let customizationId = playerSetup[key as keyof EquipmentConfiguration];
      // Check if the customizationId is overridden on the doll.
      const override = this.overrides![key as keyof EquipmentConfiguration];
      if (override !== undefined) {
        customizationId = override;
      }
      if (customizationId === null) return;
      const customizationData = getCustomizationFromId(customizationId);
      if (customizationData !== undefined) {
        if (customizationData.hidesSlots) {
          customizationData.hidesSlots.forEach((d: any) => {
            const slotObj = this.list.find((child) => child.name === `${d}`);
            if (slotObj) {
              (slotObj as Phaser.GameObjects.Sprite).setVisible(false);
            }
          });
        }
      }
    });
  }

  setCosmeticOverrides(overrides: Partial<EquipmentConfiguration>) {
    this.overrides = overrides;
    this.refresh();
  }

  handleNewCosmeticEquipped() {
    this.refresh();
  }

  /* END-USER-CODE */
}

/* END OF COMPILED CODE */

// You can write more code here
